<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_purchaseinvoice"
             src="@/assets/images/common-icons/icon_create.svg"
             title="Create purchase invoice" @click="create">
        <img v-if="currentRoutePermissions.delete_purchaseinvoice"
             src="@/assets/images/common-icons/icon_delete.svg"
             @click="deleteInvoices" title="Delete purchase invoice">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
           :aria-expanded="showCollapse"
           aria-controls="collapse">
        <h3>
          {{$t('common_content.advanced_search')}}
        </h3>
        <div class="icons-wrapper">
          <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
          <icon name="angle-right" class="angle" v-else></icon>
        </div>
      </div>
      <b-collapse v-model="showCollapse" id="collapse">
        <div class="search-form__filters">
          <div>
            <label>{{$t('labels.common.seller')}}</label>
            <multiselect v-model="searchData.sellers" :max-height="200"
                         placeholder="Choose sellers" label="name" track-by="name"
                         :options="sellers" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.invoice_number')}}</label>
            <input class="form-control" v-model="searchData.invoice_number" placeholder="Enter invoice №">
          </div>
          <div>
            <label>{{$t('labels.contracts.contract_number')}}</label>
            <multiselect v-model="searchData.contracts" :max-height="200"
                         placeholder="Choose contracts" label="contract_nr" track-by="contract_nr"
                         :options="purchaseContracts" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.invoice_status')}}</label>
            <multiselect v-model="searchData.status" :max-height="200"
                         placeholder="Choose invoice statuses" label="name" track-by="name"
                         :options="invoiceStatuses" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.supplier')}}</label>
            <multiselect v-model="searchData.suppliers" :max-height="200"
                         placeholder="Choose suppliers" label="name" track-by="name"
                         :options="suppliers" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.invoice_date')}}</label>
            <datepicker v-model="searchData.invoice_date" range range-separator="/"
                        lang="en" :first-day-of-week="1"></datepicker>
          </div>
          <div>
            <label>{{$t('labels.common.currency')}}</label>
            <multiselect v-model="searchData.currency" :max-height="200"
                         placeholder="Choose currencies" label="code" track-by="code"
                         :options="currencies" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.official_sum_payment_status')}}</label>
            <multiselect v-model="searchData.paid_status" :max-height="200"
                         placeholder="Choose invoice payment statuses" label="name" track-by="name"
                         :options="paymentStatuses" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="Choose buyers" label="name" track-by="name"
                         :options="buyers" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.receiving_date')}}</label>
            <datepicker v-model="searchData.acceptance_date" range range-separator="/"
                        lang="en" :first-day-of-week="1"></datepicker>
          </div>
          <div>
            <label>{{$t('labels.common.creator')}}</label>
            <multiselect v-model="searchData.user" :max-height="200"
                         placeholder="Choose creators" label="username" track-by="username"
                         :options="users" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.invoices.additional_sum_payment_status')}}</label>
            <multiselect v-model="searchData.paid_status_additional" :max-height="200"
                         placeholder="Choose sum payment statuses" label="name" track-by="name"
                         :options="paymentStatuses" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div>
            <label>{{$t('labels.common.agent')}}</label>
            <multiselect v-model="searchData.agents" :max-height="200"
                         placeholder="Choose agents" label="name" track-by="name"
                         :options="purchaseAgents" :multiple="true" :close-on-select="false"
                         select-label="" deselect-label="" selected-label=""></multiselect>
          </div>
          <div class="checkbox-wrapper">
            <label for="overdue">{{$t('labels.common.overdue')}}</label>
            <input id="overdue" type="checkbox" v-model="searchData.overdue">
          </div>
        </div>
      </b-collapse>
    </div>
    <div class="table-container">
      <div class="table-settings">
        <input class="form-control table-filter" placeholder="Filter..." @input="setQuickFilter">
        <div class="d-flex align-items-center">
          <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
          <input id="pagination-toggle" type="checkbox"
                 title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                 @change="togglePagination">
          <save-columns-btn :grid="gridOptions" :grid-name="gridName"
                            class="margin-left-5"></save-columns-btn>
        </div>
      </div>
      <ag-grid class="ag-theme-balham" :gridOptions="gridOptions" :columnDefs="columns" :style="gridHeight"
               :rowData="rowData" @rowDoubleClicked="goToInvoice" :pinnedBottomRowData="pinnedRowData"
               :postProcessPopup="postProcessPopup"></ag-grid>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewInvoices}} {{$t('common_content.of')}} {{invoicesCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md" :total-rows="invoicesCount" :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="changePaginateState"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapState, mapGetters } from 'vuex';
  import Datepicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';
  import {
    notificationsRenderer, formatNumber,
    LinkCellRenderer,
    MultiLinkCellRenderer,
    autoResize,
    getPinnedRowData,
    alignCells
  } from '@/helpers/renderHelpers';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    handlerResponse,
    setNotifyData,
    transformDateFromDateRangeToStringFormatByIndex,
    addColIdToColumns,
    getFilteredSelectedNodes
  } from '@/helpers/commonHelpers';

  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';

  import gridProps from '@/gridProps';
  import { PURCHASE_INVOICES } from '@/constants';
  import { gridMixins } from '@/mixins';

  export default {
    mixins: [gridMixins],
    components: {
      Datepicker,
      Multiselect,
      'ag-grid': AgGridVue,
      SaveColumnsBtn
    },
    data() {
      return {
        showCollapse: true,
        searchData: {
          sellers: [],
          suppliers: [],
          buyers: [],
          agents: [],
          currency: [],
          user: [],
          invoice_number: '',
          invoice_date: [],
          acceptance_date: [],
          overdue: false,
          contracts: [],
          status: [],
          paid_status: [],
          paid_status_additional: []
        },
        purchaseContracts: [],
        searchObject: {
          limit: 100,
          offset: 0
        },
        currentPaginateIndex: 1,
        paginationState: true,
        invoiceStatuses: [
          {key: 'draft', name: 'Draft'},
          {key: 'approved', name: 'Approved'}
        ],
        paymentStatuses: [
          {key: 'notpaid', name: 'Not paid'},
          {key: 'notfullpaid', name: 'Partly paid'},
          {key: 'paid', name: 'Paid'}
        ],
        gridOptions: {
          ...gridProps,
          rowSelection: 'multiple',
          rowGroupPanelShow: 'always',
          groupSelectsChildren: true,
          groupHideOpenParents: true,
          components: {
            'SellerLink': LinkCellRenderer('seller_id', '/partners/'),
            'BuyerLink': LinkCellRenderer('buyer_id', '/partners/'),
            'SupplierLink': LinkCellRenderer('supplier_id', '/partners/'),
            'AgentsLink': MultiLinkCellRenderer('agents', '/partners/', 'name', 'id'),
            'PurchaseInvoiceLink': LinkCellRenderer('id', '/purchase-invoice/'),
            'PurchaseContractLink': LinkCellRenderer('contract_id', '/contracts/')
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            }
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
        },
        columns: [],
        pinnedRowData: [],
        rowData: [],
        gridName: 'purchase_invoices_list',
        countOfViewInvoices: 0,
        invoicesCount: 0,
        storageKey: 'purchase-invoices'
      }
    },
    methods: {
      create() {
        this.$router.push({
          name: 'Purchase Invoice',
          params: {
            id: 'new'
          }
        });
      },
      prepareSearchData() {
        return {
          ...this.searchObject,
          sellers: getArrayByProperty(this.searchData.sellers, 'id').toString(),
          suppliers: getArrayByProperty(this.searchData.suppliers, 'id').toString(),
          buyers: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          agents: getArrayByProperty(this.searchData.agents, 'id').toString(),
          currency: getArrayByProperty(this.searchData.currency, 'id').toString(),
          user: getArrayByProperty(this.searchData.user, 'id').toString(),
          invoice_date_between_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0),
          invoice_date_between_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1),
          acceptance_date_between_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.acceptance_date, 0),
          acceptance_date_between_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.acceptance_date, 1),
          invoice_number: this.searchData.invoice_number,
          contracts: getArrayByProperty(this.searchData.contracts, 'id').toString(),
          overdue: this.searchData.overdue,
          status: getArrayByProperty(this.searchData.status, 'key').toString(),
          paid_status: getArrayByProperty(this.searchData.paid_status, 'key').toString(),
          paid_status_additional: getArrayByProperty(this.searchData.paid_status_additional, 'key').toString()
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      deleteInvoices() {
        const ids = getFilteredSelectedNodes(this.gridOptions).map(item => item.data.id);
        if (ids.length) {
          const body = {ids};
          this.$http.delete(PURCHASE_INVOICES, {body}).then(handlerResponse).then(_ => {
            this.search();
          }).catch(error => notificationsRenderer(error.body, 'error'));
        } else {
          this.$notify(setNotifyData(this.$i18n.t(`notifications.common.selected_rows_for_remove`), '', 'warn'));
        }
      },
      setQuickFilter({target: {value}}) {
        this.gridOptions.api.setQuickFilter(value);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      changePaginateState() {
        const params = {
          ...this.prepareSearchData(),
          offset: (this.currentPaginateIndex - 1) * this.searchObject.limit
        };
        this.fetchInvoices(params);
      },
      async search() {
        this.currentPaginateIndex = 1;
        const params = this.prepareSearchData();
        await this.fetchInvoices(params);
        saveFilters(this.storageKey, this.searchData);
      },
      async fetchInvoices(params) {
        try {
          this.gridOptions.api.showLoadingOverlay();
          const response = await this.$http.get(`accounting/search_for_purchase_invoices/`, {params}).then(handlerResponse);
          this.rowData = response.data.results;
          this.columns = addColIdToColumns(response.used_columns);
          this.pinnedRowData = getPinnedRowData(response.total);
          this.invoicesCount = response.data.count;
          this.countOfViewInvoices = this.rowData.length;
        } catch (e) {
          console.log(e);
        }
      },
      async fetchPurchaseContracts() {
        try {
          this.purchaseContracts = await this.$http.get(`woodware/list/purchase_contracts/`).then(handlerResponse);
        } catch (e) {
          console.log(e);
        }
      },
      goToInvoice(params) {
        if (params.data && params.data.id) {
          this.$router.push({
            name: 'Purchase Invoice',
            params: {
              id: params.data.id
            }
          });
        }
      }
    },
    computed: {
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapState('woodware', ['users']),
      ...mapGetters('woodware', ['sellers', 'purchaseAgents', 'suppliers', 'buyers']),
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 485px)'} : {height: 'calc(100vh - 245px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 245px)'};
        }

      }
    },
    created() {
      this.$store.cache.dispatch('woodware/getUsers');
      this.$store.cache.dispatch('generic/getCurrencies');
      this.$store.dispatch('woodware/getPartners', {is_active: true});
      this.$store.cache.dispatch('woodware/getUsers');
      this.fetchPurchaseContracts();
      if (checkSavedFilterData(this.storageKey)) {
        this.searchData = setFilters(this.storageKey);
      }
    }
  }
</script>

<style scoped lang="scss">
  .search-form__filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 5px 15px;
    margin-top: 10px;
  }

  .table-container {
    .table-settings {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;

      .table-filter {
        width: calc(25% - 10px);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/invoices/PurchaseInvoicesList.vue